// React, store and 3rd party
import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Components and Icons
import AddIcon from '@mui/icons-material/Add';
import RestartAlt from '@mui/icons-material/RestartAlt';
import Button from '@mui/material/Button';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';

import { Form } from 'react-final-form';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import StatusOptions from '@components/StatusOptions';
import MultiSelect, {
  REASON_CLEAR,
  REASON_REMOVE_OPTION,
  REASON_SELECT_OPTION,
} from '@components/MultiSelect';
import { Typography } from '@components/styledComponents';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import {
  editGatewayMapping,
  LOB_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';
import {
  getGateways,
  selectGateways,
  updateGatewayMapping,
} from '../../store/payment-options-gateways';
import {
  getCurrencies,
  selectCurrencies,
} from '../../store/payment-options-currencies';
import {
  getLOBDetails,
  getLOBs,
  selectLOBList,
} from '../../store/payment-options-merchants';
import {
  getPaymentMethods,
  selectMethodList,
} from '../../store/payment-options-methods';

import SelectedMerchantsTable from '../../components/SelectedMerchantsTable';
import styles from './Gateway.module.css';

export default function GatewayMapping() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = usePermissions();

  // Edit-only page: the tab is hidden for view-only users, but the route is
  // still reachable by URL.
  useEffect(() => {
    if (!permissions?.canEditPaymentOptions) {
      navigate(LOB_LIST_ROUTE);
    }
  }, [navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [selectedLOBMerchants, setSelectedLOBMerchants] = useState([]);
  const [showMerchantList, setShowMerchantList] = useState(false);
  const [showAddMethodBtn, setShowAddMethodBtn] = useState(false);
  const [addedMerchants, setAddedMerchants] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  const selectedMerchants = useRef({ lob: null, merchants: [] });
  const formValue = useRef({
    lob: null,
    merchants: null,
    currencies: [],
    methods: [],
    gateway: null,
    status: 1,
  });

  const gatewayList = useSelector(selectGateways);
  const currencyList = useSelector(selectCurrencies);
  const lobList = useSelector(selectLOBList);
  const methodList = useSelector(selectMethodList);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(editGatewayMapping));
  }, [dispatch]);

  useEffect(() => {
    setIsFetchingData(true);
    return Promise.all([
      dispatch(getGateways()),
      dispatch(getCurrencies()),
      dispatch(getPaymentMethods()),
      dispatch(getLOBs()),
    ]).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  useEffect(
    () => setShowTable(addedMerchants && addedMerchants.length > 0),
    [addedMerchants],
  );

  const resetStep1 = useCallback(() => {
    selectedMerchants.current = { lob: null, merchants: [] };
    formValue.current = { ...formValue.current, lob: null, merchants: null };
    setShowMerchantList(false);
    setShowAddMethodBtn(false);
    setSelectedLOBMerchants([]);
  }, [selectedMerchants, formValue]);

  const handleAddMerchant = useCallback(() => {
    const newLobId = selectedMerchants.current.lob.lobId;
    const newMerchants = selectedMerchants.current.merchants;

    const existingIdx = addedMerchants.findIndex(
      (merchant) => merchant.lob.lobId === newLobId,
    );
    let updatedMerchants = addedMerchants.concat(selectedMerchants.current);

    if (existingIdx !== -1) {
      updatedMerchants = [...addedMerchants];
      updatedMerchants[existingIdx] = {
        ...selectedMerchants.current,
        merchants: newMerchants,
      };
    }
    setAddedMerchants(updatedMerchants);
    resetStep1();
  }, [selectedMerchants, addedMerchants, resetStep1]);

  const handleRemoveMerchant = useCallback((lobId, setValue) => {
    setAddedMerchants((current) => {
      const filteredCurrent = current.filter(({ lob }) => lob.lobId !== lobId);
      if (!filteredCurrent || filteredCurrent.length === 0) {
        setValue('lob', null);
        setValue('merchants', []);
      }
      return filteredCurrent;
    });
  }, []);

  const handleOnChangeLOB = useCallback(
    (value, reason) => {
      if (reason === REASON_CLEAR || value === null) {
        resetStep1();
        return;
      }

      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowMerchantList(false);

        dispatch(getLOBDetails(value.lobId)).then(
          ({ payload: { merchantDetails } }) => {
            setSelectedLOBMerchants(merchantDetails);

            const lobExistsIdx = addedMerchants.findIndex(
              (merchant) => merchant.lob.lobId === value.lobId,
            );

            // reselect existing merchants
            selectedMerchants.current = {
              lob: value,
              merchants:
                lobExistsIdx > -1 ? addedMerchants[lobExistsIdx].merchants : [],
            };
            setShowMerchantList(true);
          },
        );
      }
    },
    [dispatch, resetStep1, selectedMerchants, addedMerchants],
  );

  const handleOnChangeMerchants = useCallback(
    (values, reason) => {
      if (reason === REASON_CLEAR) {
        setShowAddMethodBtn(false);
      }
      if (reason === REASON_SELECT_OPTION || reason === REASON_REMOVE_OPTION) {
        setShowAddMethodBtn(values && values.length > 0);
      }
      selectedMerchants.current = {
        ...selectedMerchants.current,
        merchants: values,
      };
    },
    [selectedMerchants],
  );

  const onSubmit = useCallback(
    (values) => {
      setFormSuccessMsg('');
      setFormErrorMsg('');

      const { currencies, methods, gateway, status } = values;

      const payload = {
        merchantIds: addedMerchants.reduce((prev, { merchants }) => {
          const lobMerchants = merchants.map((merchant) => merchant.merchantId);
          return prev.concat(lobMerchants);
        }, []),
        gatewayId: gateway.id,
        status,
      };

      if (currencies && currencies.length > 0) {
        const source = currencies[0].id !== 0 ? currencies : currencyList;

        payload.currencyIds = source.map((currency) => currency.id);
      }

      if (methods && methods.length > 0) {
        const source = methods[0].id !== 0 ? methods : methodList;

        payload.methodIds = source.map((method) => method.methodId);
      }

      if (!payload.merchantIds || !payload.merchantIds.length === 0) return;

      dispatch(updateGatewayMapping(payload)).then((data) => {
        if (data.payload && data.payload.status === 'SUCCESS') {
          setFormSuccessMsg('Gateway mapping has been updated successfully');
        } else {
          setFormErrorMsg(data.payload?.message || data?.error.message);
        }
      });
    },
    [addedMerchants, currencyList, dispatch, methodList],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};
      if (!values.lob || addedMerchants.length === 0) {
        errors.lob = 'LOB is required';
      }
      if (!values.merchants || values.merchants.length === 0) {
        errors.merchants = 'Merchants are required';
      }
      if (!values.currencies || values.currencies.length === 0)
        errors.currencies = 'Currencies are required';
      if (!values.methods || values.methods.length === 0)
        errors.methods = 'Methods are required';
      if (!values.gateway) errors.gateway = 'Gateway is required';
      return errors;
    },
    [addedMerchants],
  );

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Gateway Mapping"
      description="Bulk-editing of merchant-method-currency mapping to a gateway"
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue.current}
          validate={validate}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({
            handleSubmit,
            pristine,
            submitting,
            form: {
              mutators: { setValue },
            },
            values,
          }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />{' '}
              <Typography
                className="form-header"
                variant="formHeader">
                Step 1
              </Typography>
              {showTable && (
                <SelectedMerchantsTable
                  merchants={addedMerchants}
                  handleRemoveMerchant={(lobId) =>
                    handleRemoveMerchant(lobId, setValue)
                  }
                />
              )}
              <Grid
                container
                sx={{ marginTop: '0' }}
                spacing={2}>
                <Grid
                  item
                  xs={3}>
                  <MultiSelect
                    id="lobList"
                    name="lob"
                    items={lobList}
                    value={selectedMerchants.current.lob}
                    isOptionEqualToValue={(option, value) =>
                      option.lobId === value.lobId
                    }
                    multiple={false}
                    label="Select LOB"
                    optionDisplayKey="lobName"
                    onChange={handleOnChangeLOB}
                  />
                </Grid>
                {showMerchantList && (
                  <Grid
                    item
                    xs={9}
                    md={5}>
                    <MultiSelect
                      id="merchantList"
                      name="merchants"
                      items={selectedLOBMerchants}
                      value={selectedMerchants.current.merchants}
                      isOptionEqualToValue={(option, value) =>
                        option.merchantId === value.merchantId
                      }
                      label="Select platforms"
                      optionDisplayKey="platformName"
                      onChange={handleOnChangeMerchants}
                    />
                  </Grid>
                )}
                {showAddMethodBtn && (
                  <Grid
                    item
                    xs={12}
                    md={4}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<RestartAlt />}
                      variant="outlined"
                      type="button"
                      className={styles.Button}
                      onClick={resetStep1}>
                      Reset
                    </Button>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<AddIcon />}
                      variant="contained"
                      type="button"
                      className={styles.Button}
                      onClick={handleAddMerchant}>
                      Add Merchant
                    </Button>
                  </Grid>
                )}
              </Grid>
              <Typography
                className="form-header"
                variant="formHeader">
                Step 2
              </Typography>
              <Grid
                container
                spacing={2}>
                <Grid
                  item
                  xs={12}>
                  <MultiSelect
                    id="methodOptions"
                    name="methods"
                    items={methodList}
                    label="Select methods"
                    optionDisplayKey="methodCode"
                    customOptionLabel={(option) =>
                      `${option.methodName} (${option.methodCode})`
                    }
                    customFilterStringify={(option) =>
                      `${option.methodName} (${option.methodCode})`
                    }
                  />
                </Grid>
              </Grid>
              <Typography
                className="form-header"
                variant="formHeader">
                Step 3
              </Typography>
              <Grid
                container
                spacing={2}>
                <Grid
                  item
                  xs={12}>
                  <MultiSelect
                    id="currencyOptions"
                    name="currencies"
                    items={currencyList}
                    optionDisplayKey="code"
                    customOptionLabel={(option) =>
                      `${option.name} (${option.code})`
                    }
                    customFilterStringify={(option) =>
                      `${option.name} (${option.code})`
                    }
                    label="Select currencies"
                  />
                </Grid>
              </Grid>
              <Typography
                className="form-header"
                variant="formHeader">
                Step 4
              </Typography>
              <MultiSelect
                id="gateWayOptions"
                name="gateway"
                disableSelectAll
                multiple={false}
                items={gatewayList}
                label="Select gateway"
                openText="The gateway where the selected methods will be pointed to"
              />
              <Typography
                className="form-header"
                variant="formHeader">
                Step 5
              </Typography>
              <StatusOptions
                value={values.status}
                onChange={(newValue) => setValue('status', newValue)}
              />
              {formSuccessMsg && (
                <Alert
                  className={styles.alerts}
                  onClose={() => {
                    setFormSuccessMsg('');
                  }}
                  severity="success">
                  {formSuccessMsg}
                </Alert>
              )}
              {formErrorMsg && (
                <Alert
                  className={styles.alerts}
                  onClose={() => {
                    setFormErrorMsg('');
                  }}
                  severity="error">
                  {formErrorMsg}
                </Alert>
              )}
              <Button
                id="submitBtn"
                style={{ marginTop: '32px' }}
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                disabled={
                  pristine || submitting || !permissions?.canEditPaymentOptions
                }>
                Submit
              </Button>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
